import IPPageNumber from "app/pages/InteractiveProposal/InteractiveProposalDownload/IPPageNuber";
import HexagonImg from "app/pages/InteractiveProposal/images/hexagonImg.png";
import { getSessionStore } from "app/utils/sessionStore";
import { getThemeColors } from "app/utils/utils";
import { getColorVariations } from "app/utils/colorUtils";
import PFApendixModalJson, { headerColumnGroupPFAppendixModal } from "app/Data/PFApendixModalJson";

interface IRCAppendixPageProps {
  performance: any[];
  insuranceCompanyName?: string;
  pageNumber: string | number;
  isNewPage?: boolean;
}

const ALLOWED_FIELDS = [
  "planYear",
  "clientPremium",
  "clientAdminFee",
  "clientInterestPayment",
  "clientOriginationFees",
  "lenderPremium",
  "totalPolicyPremium",
  "eoyOutstandingLoanBalance",
  "stressTestedCollateral",
  "estimatedLoanPayoffOrIncome",
  "illustratedPlanAccumulatedValue",
  "illustratedPlanSurrenderValue",
  "illustratedPlanDB",
];

const UNLABELED_FIELDS = ["planYear"];
const groupKeys = Object.keys(headerColumnGroupPFAppendixModal);
const allGroups = [
  { label: "", fields: UNLABELED_FIELDS },
  ...groupKeys.map((key) => ({
    label: key,
    fields: headerColumnGroupPFAppendixModal[key as keyof typeof headerColumnGroupPFAppendixModal],
  })),
];

const filteredGroups = allGroups
  .map((g) => ({ ...g, fields: g.fields.filter((f) => ALLOWED_FIELDS.includes(f)) }))
  .filter((g) => g.fields.length > 0);

const orderedColumns = filteredGroups.flatMap((g) =>
  g.fields.map((field) => PFApendixModalJson.find((c) => c.field === field)!).filter(Boolean),
);

const IRCAppendixPage = ({
  performance,
  insuranceCompanyName,
  pageNumber,
  isNewPage = false,
}: IRCAppendixPageProps) => {
  const { branding } = getSessionStore("lookUpResponse");
  const { secondaryColor } = getThemeColors(branding);
  const groupColors = getColorVariations(secondaryColor, filteredGroups.length).reverse();

  const fieldColorMap: Record<string, string> = {};
  filteredGroups.forEach((g, gi) => {
    g.fields.forEach((f) => {
      fieldColorMap[f] = groupColors[gi];
    });
  });

  return (
    <div className="flex flex-col h-full relative min-h-screen">
      <div className="relative z-20 px-[2em] w-full max-w-[56em] mx-auto p-[1.75em]">
        {!isNewPage && (
          <>
            <div className="text-xl font-bold mb-3 text-center interactive-proposal-text conthrax-font">
              Appendix
            </div>
            {insuranceCompanyName && (
              <p className="mb-2 text-base conthrax-font" style={{ color: "#495057" }}>
                {insuranceCompanyName} – Please see full insurance company illustration
              </p>
            )}
          </>
        )}

        <table className="w-full border-collapse">
          <thead className="text-white">
            <tr>
              {orderedColumns.map((col, ci) => (
                <th
                  key={ci}
                  className="p-1 text-center pdf_table_border border-white font-semibold"
                  style={{ backgroundColor: fieldColorMap[col.field], fontSize: "8px" }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {performance?.map((row, i) => (
              <tr
                key={i}
                style={{
                  backgroundColor: i % 2 === 0 ? "#EEEEEE" : "#FFFFFF",
                  color: "#000000",
                  fontSize: "8px",
                }}
              >
                {orderedColumns.map((col, ci) => {
                  const value = row[col.field];
                  const formatted = col.formatter ? col.formatter(value) : value ?? "—";
                  return (
                    <td key={ci} className="p-1 text-center pdf_table_border border-white">
                      {formatted}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="absolute bottom-0 left-0 w-full">
        <div
          className="h-[350px] bg-cover bg-center"
          style={{ backgroundImage: `url(${HexagonImg})` }}
        />
        <div className="px-[4em] w-full max-w-[56em] mx-auto p-[1.75em] pt-1">
          <IPPageNumber number={pageNumber} />
        </div>
      </div>
    </div>
  );
};

export default IRCAppendixPage;
